import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PlatformEnum } from '@panamax/app-state';
import { calendarcompatibleDateFormat } from '@shared/constants/date-formats';
import { allDaysAllowed } from '@shared/constants/dayConstraints';
import {
  areDatesEqual,
  convertStringToDate,
  datesMatch,
  isDateGreater,
  MultiSelectMode,
} from '@shared/helpers/calendar.helpers';

@Component({
  selector: 'app-calendar-v2',
  templateUrl: './calendar-v2.component.html',
  styleUrls: ['./calendar-v2.component.scss'],
})
export class CalendarV2Component implements OnInit {
  @Input() title: string;
  // default to 5 years
  @Input() maxLimit = 1325;
  // default to 6 days for selecting a week of data
  @Input() maxRange = 6;
  @Input() dayValues = allDaysAllowed;
  @Input() platform: PlatformEnum;

  // multiSelectMode: range unlimited, range limited, multiSelect
  @Input() multiSelectMode: MultiSelectMode = MultiSelectMode.IonicDefault;

  @Output() rangeEmit: EventEmitter<string[]> = new EventEmitter();

  dateOne: Date;
  dateTwo: Date;

  maxDate: string;
  maxDateReference: Date;
  minDate: string;
  minDateReference: Date;
  selections: string[];
  modeReference = MultiSelectMode;
  platformReference = PlatformEnum;
  constructor(private datepipe: DatePipe) {}

  ngOnInit() {
    this.initialMaxDateSetup();
  }

  initialMaxDateSetup() {
    const currentDate = new Date();

    const tempMinDate = new Date(currentDate);
    tempMinDate.setDate(tempMinDate.getDate() - this.maxLimit);
    this.minDate = tempMinDate.toISOString();
    this.minDateReference = tempMinDate;

    const tempMaxDate = new Date(currentDate);
    tempMaxDate.setDate(tempMaxDate.getDate() + this.maxLimit);
    this.maxDate = tempMaxDate.toISOString();
    this.maxDateReference = tempMaxDate;
  }

  public handleValue($event: any) {
    // month switching causes a null nrNode and force adds a value
    if ($event.__nrNode === null) {
      if ($event.detail.value !== undefined) {
        let valueToForce = [...($event.detail.value as string[])];
        valueToForce.pop();
        this.updateRangeDirectly(valueToForce);
      }

      $event.preventDefault();

      return;
    }

    if ($event.detail.value === undefined || $event.detail.value.length === 0) {
      if (this.dateOne) {
        this.reset();
      }
      return;
    }

    switch (this.multiSelectMode) {
      case MultiSelectMode.Limited:
        this.selectForRangeAlternative($event.detail.value);
        break;
      case MultiSelectMode.Unlimited:
        this.selectForRange($event.detail.value);
        break;

      case MultiSelectMode.IonicDefault:
        this.perserveDefaultBehavior($event.detail.value);
        break;
    }
  }

  // Ionic default multi selecct
  private perserveDefaultBehavior(desiredValue: string[]) {
    // Range was not updating via databind for range selection.
    const datetime = document.querySelector('ion-datetime');
    datetime.onchange = null;
    datetime.value = desiredValue;
    this.selections = desiredValue;
    datetime.onchange = this.handleValue;
    this.emitRange();
  }

  private updateRangeDirectly(desiredValue: string[]) {
    // Range was not updating via databind for range selection.
    const datetime = document.querySelector('ion-datetime');
    datetime.onchange = null;
    datetime.value = desiredValue;
    datetime.onchange = this.handleValue;
  }

  resrictionBasedOnSelection() {
    if (this.dateTwo === undefined) {
      const maxDate = new Date(this.dateOne);
      maxDate.setDate(maxDate.getDate() + this.maxRange);
      const minDate = new Date(this.dateOne);
      minDate.setDate(minDate.getDate() - this.maxRange);
      if (isDateGreater(minDate, this.minDateReference)) {
        this.minDate = minDate.toISOString();
      } else {
        this.minDate = this.minDateReference.toISOString();
      }
      if (isDateGreater(this.maxDateReference, maxDate)) {
        this.maxDate = maxDate.toISOString();
      } else {
        this.maxDate = this.maxDateReference.toISOString();
      }
    } else {
      this.maxDate = this.dateTwo.toISOString();
      this.minDate = this.dateOne.toISOString();
    }
  }

  public reset() {
    this.selections = [];
    this.dateOne = undefined;
    this.dateTwo = undefined;
    this.initialMaxDateSetup();
    try {
      const datetime = document.querySelector('ion-datetime');
      datetime.blur();
    } catch (ignore) {}
    this.emitRange();
  }

  private convertDateToString(date: Date) {
    return this.datepipe.transform(date, calendarcompatibleDateFormat);
  }

  private setDateOne(dates: string[]) {
    this.dateOne = convertStringToDate(dates[0]);
    this.selections = [this.convertDateToString(this.dateOne)];
  }

  private setDateTwo(dates: string[]) {
    this.dateTwo = convertStringToDate(dates[1]);
    this.selections.push(this.convertDateToString(this.dateTwo));
  }

  fillAndSetRange() {
    let tempDate: Date = isDateGreater(this.dateOne, this.dateTwo)
      ? new Date(this.dateTwo)
      : new Date(this.dateOne);
    let maxDate: Date = isDateGreater(this.dateOne, this.dateTwo)
      ? new Date(this.dateOne)
      : new Date(this.dateTwo);
    this.dateOne = new Date(tempDate);
    this.dateTwo = new Date(maxDate);
    this.selections = [this.convertDateToString(tempDate)];

    tempDate.setDate(tempDate.getDate() + 1);
    while (isDateGreater(maxDate, tempDate)) {
      this.selections.push(this.convertDateToString(tempDate));
      tempDate.setDate(tempDate.getDate() + 1);
    }
    this.selections.push(this.convertDateToString(maxDate));
    this.updateRangeDirectly(this.selections);
    this.emitRange();
  }

  // no limits beyond the initial max range
  selectForRange(dates: string[]) {
    if (datesMatch(this.selections, dates)) return;

    if (this.dateOne === undefined) {
      this.setDateOne(dates);
    } else if (
      this.dateTwo === undefined &&
      dates.length > 1 &&
      !areDatesEqual(new Date(dates[1]), new Date(dates[0]))
    ) {
      this.setDateTwo(dates);
      this.fillAndSetRange();
    } else if (this.dateTwo !== undefined) {
      this.dateOne = convertStringToDate(dates[dates.length - 1]);
      this.dateTwo = undefined;
      this.selections = [this.convertDateToString(this.dateOne)];
    }
  }

  // limits selection by a max date
  selectForRangeAlternative(dates: string[]) {
    if (this.dateOne === undefined) {
      this.setDateOne(dates);
      this.resrictionBasedOnSelection();
    } else if (
      this.dateTwo === undefined &&
      dates.length > 1 &&
      !areDatesEqual(new Date(dates[1]), new Date(dates[0]))
    ) {
      this.setDateTwo(dates);
      this.fillAndSetRange();
      this.resrictionBasedOnSelection();
    } else if (this.dateTwo !== undefined) {
      this.reset();
    }
  }

  emitRange() {
    this.rangeEmit.emit(this.selections);
  }
}
